import React, { useMemo } from "react";
import { css } from "@emotion/react";
import { useQuery } from "react-query";

import { axiosInstance } from "@src/api/axiosConfig";
import { Common } from "@src/styles/Common";

/** 법원은 새 기일을 지정했는데 화면은 옛 날짜인 물건 (2026-09-09 라이브 반영된 어드민 API) */
interface StaleBidDateItem {
    itemId?: string;
    subId?: string;
    courtName?: string;
    status?: string;
    /** items-v3.bid_date 원문 (예 "2026.08.31 10:00") */
    screenBidDate?: string;
    /** 화면 매각기일 YYYYMMDD */
    screenMaeGiil?: string;
    /** 원천(auction_calendar.maeGiil) 최신 매각기일 YYYYMMDD */
    sourceMaeGiil?: string;
    gapDays?: number;
    /** 원천 기일이 아직 오지 않았나 — true 면 지금 화면이 틀린 상태 */
    isSourceUpcoming?: boolean;
    latestStage?: string;
    latestResult?: string;
    address?: string;
    updatedAt?: string;
}

interface StaleBidDateItemsDTO {
    totalCount: number;
    currentPage: number;
    pageSize: number;
    list: StaleBidDateItem[];
}

// 자동화 QA 페이지(develop 전용)에 의존하지 않도록 이 카드 안에서 직접 부른다
const fetchStaleBidDateItems = async (limit: number): Promise<StaleBidDateItemsDTO> => {
    const { data } = await axiosInstance.get("qa/dashboard/stale-bid-date-items", {
        params: { page: 1, limit },
    });
    return data;
};

/** 임박도별 색. 오늘·1~3일이 눈에 먼저 들어와야 한다 */
const URGENT = "#F45B5B";
const SOON = Common.colors.orange;

/** "20260909" → "09.09" (같은 해는 월·일만 보여 표가 좁아진다) */
const monthDay = (ymd?: string): string => (ymd && ymd.length === 8 ? `${ymd.slice(4, 6)}.${ymd.slice(6)}` : "-");

/** "20250917" → "25.09.17" (다른 해가 섞이므로 연도를 남긴다) */
const shortYmd = (ymd?: string): string =>
    ymd && ymd.length === 8 ? `${ymd.slice(2, 4)}.${ymd.slice(4, 6)}.${ymd.slice(6)}` : "-";

const todayYmd = (): string => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
};

/** YYYYMMDD 두 개의 날짜 차이(일). 문자열 뺄셈이 아니라 실제 날짜로 센다 */
const daysBetween = (fromYmd: string, toYmd: string): number => {
    const at = (s: string) => new Date(Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6)));
    return Math.round((at(toYmd).getTime() - at(fromYmd).getTime()) / 86400000);
};

const ddayLabel = (n: number): string => (n === 0 ? "D-Day" : n > 0 ? `D-${n}` : `+${-n}일`);

const ddayColor = (n: number): string => {
    if (n === 0) return URGENT;
    if (n >= 1 && n <= 3) return SOON;
    if (n >= 4 && n <= 7) return SOON;
    return Common.colors.gray700;
};

/**
 * 법원은 변경 후 새 기일을 지정했는데 미스고 화면은 옛 날짜인 물건.
 *
 * 기존 QA 대조는 status 만 비교해서(getDiffStatus) 기일이 어긋나도 잡히지 않았다.
 * 2026-09-09 전수조사에서 7,567건이 나왔고 그중 446건은 원천 기일이 아직 오지 않은 날짜였다.
 * 「기일이 며칠 남았는지」가 실제로 급한 정보라 D-day 를 맨 위·표 첫 열에 둔다.
 */
function StaleBidDateCard() {
    // 차이 큰 것부터 오지만, 카드에서는 「기일이 임박한 것」이 먼저 보여야 한다.
    // 서버 정렬을 그대로 쓰면 2년 전 건이 위에 오므로 넉넉히 받아 여기서 다시 세운다.
    const { data, isError, isLoading } = useQuery<StaleBidDateItemsDTO, Error>(["staleBidDateItems", 100], () =>
        fetchStaleBidDateItems(100),
    );

    const today = useMemo(todayYmd, []);

    const rows = useMemo(() => {
        const list = data?.list ?? [];
        return list
            .filter((r) => !!r.sourceMaeGiil && !!r.screenMaeGiil)
            .map((r) => ({ ...r, dday: daysBetween(today, r.sourceMaeGiil as string) }))
            .sort((a, b) => {
                // 기일이 아직 안 온 것 먼저, 그중 임박한 순
                const aUp = a.dday >= 0 ? 0 : 1;
                const bUp = b.dday >= 0 ? 0 : 1;
                if (aUp !== bUp) return aUp - bUp;
                return aUp === 0 ? a.dday - b.dday : b.dday - a.dday;
            });
    }, [data, today]);

    const upcoming = useMemo(() => rows.filter((r) => r.dday >= 0), [rows]);

    const buckets = useMemo(() => {
        const inRange = (lo: number, hi: number) => upcoming.filter((r) => r.dday >= lo && r.dday <= hi).length;
        return [
            { n: inRange(0, 0), label: "오늘 입찰", tone: "today" as const },
            { n: inRange(1, 3), label: "1~3일", tone: "soon" as const },
            { n: inRange(4, 7), label: "4~7일", tone: "soon" as const },
            { n: upcoming.filter((r) => r.dday >= 8).length, label: "8일 이후", tone: "plain" as const },
            { n: rows.length - upcoming.length, label: "기일 지남", tone: "past" as const },
        ];
    }, [rows, upcoming]);

    const asOf = useMemo(() => {
        const d = new Date();
        const p = (n: number) => String(n).padStart(2, "0");
        return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(
            d.getMinutes(),
        )} 기준`;
    }, []);

    return (
        <div css={cardStyle}>
            <div css={headRow}>
                <p css={titleP}>법원 기일 지정 · 미스고 미반영</p>
                <span css={asOfSpan}>{asOf}</span>
            </div>
            <p css={subP}>법원은 변경 후 새 기일을 지정했는데 미스고 화면은 옛 날짜인 물건</p>

            {isError ? (
                <div css={messageStyle}>데이터 조회에 실패하였습니다.</div>
            ) : (
                <>
                    <div css={ddayRow}>
                        {buckets.map((b) => (
                            <div key={b.label} css={[ddayCell, ddayTone[b.tone]]}>
                                <strong css={[ddayNum, ddayNumTone[b.tone]]}>{isLoading ? "-" : b.n}</strong>
                                <span css={ddayLab}>{b.label}</span>
                            </div>
                        ))}
                    </div>

                    <div css={tableHead}>
                        <span css={tableTitle}>{`입찰 임박순 · ${upcoming.length}건`}</span>
                        <span css={tableNote}>취소선이 지금 화면에 뜨는 값</span>
                    </div>

                    <div css={tableWrap}>
                        <table css={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={{ width: 56 }}>기일</th>
                                    <th style={{ width: 70 }}>법원 기일</th>
                                    <th style={{ width: 80 }}>미스고</th>
                                    <th style={{ width: 116 }}>법원</th>
                                    <th>사건번호</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} css={emptyTd}>
                                            {isLoading ? "불러오는 중..." : "어긋난 물건이 없습니다."}
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((r) => (
                                        <tr key={r.itemId}>
                                            <td css={ddayTd} style={{ color: ddayColor(r.dday) }}>
                                                {ddayLabel(r.dday)}
                                            </td>
                                            <td css={courtYmdTd}>{monthDay(r.sourceMaeGiil)}</td>
                                            <td css={oursYmdTd}>{shortYmd(r.screenMaeGiil)}</td>
                                            <td css={courtTd}>{r.courtName ?? "-"}</td>
                                            <td css={caseTd}>{r.subId ?? "-"}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div css={footRow}>
                        <span css={listNote}>차이가 큰 것부터 · 아래로 스크롤</span>
                        <span css={totalSpan}>{`전체 미반영 ${(data?.totalCount ?? 0).toLocaleString()}건`}</span>
                    </div>
                </>
            )}
        </div>
    );
}

/* 카드 규격은 홈의 다른 차트 카드와 같게 둔다 (OnbidGuestStatsChart 기준) */
const cardStyle = css`
    width: 610px;
    height: 360px;
    padding: 16px;
    margin-bottom: 20px;
    border-radius: 8px;
    border: 1.5px solid ${Common.colors.gray200};
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const headRow = css`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
`;

const titleP = css`
    ${Common.textStyle.title7_M12}
    color: ${Common.colors.black};
    font-size: 15px;
    margin: 0;
`;

const asOfSpan = css`
    font-size: 11px;
    color: ${Common.colors.gray500};
    font-variant-numeric: tabular-nums;
`;

const subP = css`
    font-size: 11.5px;
    color: ${Common.colors.gray600};
    margin: 2px 0 12px;
`;

const ddayRow = css`
    display: flex;
    gap: 6px;
    margin-bottom: 12px;
`;

const ddayCell = css`
    flex: 1;
    border: 1px solid ${Common.colors.gray200};
    border-radius: 6px;
    padding: 8px 6px;
    text-align: center;
`;

const ddayTone = {
    today: css`
        border-color: #ffc9b4;
        background: #fff3ed;
    `,
    soon: css`
        border-color: #ffe0cc;
        background: #fff9f5;
    `,
    plain: css``,
    past: css`
        background: ${Common.colors.gray100};
    `,
};

const ddayNum = css`
    display: block;
    font-size: 19px;
    font-weight: 800;
    line-height: 1.15;
    font-variant-numeric: tabular-nums;
`;

const ddayNumTone = {
    today: css`
        color: ${URGENT};
    `,
    soon: css`
        color: ${SOON};
    `,
    plain: css`
        color: ${Common.colors.black};
    `,
    past: css`
        color: ${Common.colors.gray600};
    `,
};

const ddayLab = css`
    font-size: 10.5px;
    color: ${Common.colors.gray600};
    margin-top: 2px;
    display: block;
`;

const tableHead = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 5px;
`;

const tableTitle = css`
    font-size: 11.5px;
    font-weight: 700;
    color: ${Common.colors.black};
`;

const tableNote = css`
    font-size: 10.5px;
    color: ${Common.colors.gray500};
`;

const tableWrap = css`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    ::-webkit-scrollbar {
        width: 2px;
    }
    ::-webkit-scrollbar-thumb {
        border-radius: 20px;
        background-color: #c9c9c9;
    }
`;

const tableStyle = css`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    table-layout: fixed;

    thead th {
        font-size: 10.5px;
        font-weight: 600;
        color: ${Common.colors.gray600};
        text-align: left;
        padding: 0 5px 6px;
        border-bottom: 1px solid ${Common.colors.gray200};
        white-space: nowrap;
    }

    tbody td {
        padding: 6px 5px;
        border-bottom: 1px solid ${Common.colors.gray100};
        color: ${Common.colors.gray700};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    tbody tr:hover {
        background: ${Common.colors.gray100};
    }
`;

const ddayTd = css`
    font-weight: 800;
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
`;

const courtYmdTd = css`
    font-variant-numeric: tabular-nums;
    color: ${Common.colors.black};
    font-weight: 700;
`;

/* 지금 화면에 잘못 뜨는 값 — 취소선으로 어느 쪽이 틀렸는지 바로 보이게 */
const oursYmdTd = css`
    font-variant-numeric: tabular-nums;
    color: ${URGENT};
    text-decoration: line-through;
`;

const courtTd = css`
    color: ${Common.colors.black};
`;

const caseTd = css`
    color: ${Common.colors.gray600};
    font-size: 11px;
`;

const emptyTd = css`
    text-align: center;
    padding: 28px 0 !important;
    color: ${Common.colors.gray600};
`;

const footRow = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 8px;
`;

const listNote = css`
    font-size: 11px;
    color: ${Common.colors.gray500};
`;

const totalSpan = css`
    font-size: 11px;
    color: ${Common.colors.gray600};
`;

const messageStyle = css`
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
    ${Common.textStyle.title7_M12}
    color: ${Common.colors.gray700};
`;

export default StaleBidDateCard;
